import React, { useState, useEffect, CSSProperties } from 'react';
import styled from 'styled-components';
import { Button, Icon } from 'semantic-ui-react';
import { PDFViewerCore, PdfViewerProps } from './PDFViewerCore';

const DesktopPdfViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #f7f8fb;
`;

const DesktopPdfViewerContent = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const PageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2f4f74;
  font-size: 0.95rem;
`;

export type DesktopPDFViewerProps = PdfViewerProps & {
  style?: CSSProperties;
  isFullScreen?: boolean;
};

export type { PdfViewerProps };

export const DesktopPDFViewer: React.FC<DesktopPDFViewerProps> = ({
  blob,
  style,
  viewMode: initialViewMode,
  scale: initialScale,
  onViewModeChange,
  onScaleChange,
  ...props
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'scale' | 'fitWidth' | 'fullPage'>(initialViewMode ?? 'fitWidth');
  const [scale, setScale] = useState(initialScale ?? 1);

  useEffect(() => {
    if (initialViewMode !== undefined) {
      setViewMode(initialViewMode);
    }
  }, [initialViewMode]);

  useEffect(() => {
    if (initialScale !== undefined) {
      setScale(initialScale);
    }
  }, [initialScale]);

  useEffect(() => {
    if (blob) {
      setPageNumber(1);
      setNumPages(null);
    }
  }, [blob]);

  useEffect(() => {
    if (numPages && pageNumber > numPages) {
      setPageNumber(numPages);
    }
  }, [numPages, pageNumber]);

  const handleViewModeChange = (mode: 'scale' | 'fitWidth' | 'fullPage') => {
    setViewMode(mode);
    onViewModeChange?.(mode);
  };

  const handleScaleChange = (nextScale: number) => {
    setScale(nextScale);
    onScaleChange?.(nextScale);
  };

  const handlePageChange = (nextPage: number) => {
    setPageNumber(nextPage);
  };

  const handleDocumentPages = (documentPages: number) => {
    setNumPages(documentPages);
  };

  const toggleFitMode = () => {
    const nextMode = viewMode === 'fullPage' ? 'fitWidth' : 'fullPage';
    handleViewModeChange(nextMode);
    handleScaleChange(1);
  };

  const changePage = (delta: number) => {
    if (!numPages) return;
    setPageNumber(prev => Math.min(Math.max(1, prev + delta), numPages));
  };

  return (
    <DesktopPdfViewerContainer style={style}>
      <Toolbar>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button icon color="blue" onClick={() => handleScaleChange(Math.max(0.2, scale - 0.1))} title="Zoom out">
            <Icon name="search minus" />
          </Button>
          <Button icon color="green" onClick={() => handleScaleChange(Math.min(3, scale + 0.1))} title="Zoom in">
            <Icon name="search plus" />
          </Button>
          <Button icon onClick={toggleFitMode} title={viewMode === 'fullPage' ? 'Fit width' : 'Full page'}>
            <Icon name={viewMode === 'fullPage' ? 'arrows alternate horizontal' : 'window maximize'} />
          </Button>
        </div>

        <PageInfo>
          <Button icon disabled={pageNumber <= 1} onClick={() => setPageNumber(1)} title="First page">
            <Icon name="angle double left" />
          </Button>
          <Button icon disabled={pageNumber <= 1} onClick={() => changePage(-1)} title="Previous page">
            <Icon name="angle left" />
          </Button>
          <span>{pageNumber}</span>
          <span>of</span>
          <span>{numPages ?? '-'}</span>
          <Button icon disabled={!numPages || pageNumber >= numPages} onClick={() => changePage(1)} title="Next page">
            <Icon name="angle right" />
          </Button>
          <Button icon disabled={!numPages || pageNumber >= numPages} onClick={() => numPages && setPageNumber(numPages)} title="Last page">
            <Icon name="angle double right" />
          </Button>
        </PageInfo>
      </Toolbar>

      <DesktopPdfViewerContent>
        <PDFViewerCore
          blob={blob}
          pageNumber={pageNumber}
          scale={viewMode === 'scale' ? scale : scale}
          viewMode={viewMode}
          onPageChange={handlePageChange}
          onScaleChange={handleScaleChange}
          onViewModeChange={handleViewModeChange}
          onNumPagesChange={handleDocumentPages}
          style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}
          {...props}
        />
      </DesktopPdfViewerContent>
    </DesktopPdfViewerContainer>
  );
};

export default DesktopPDFViewer;
